// stageCuts — a song's cuts for scene view at the plaza stage, from its measured
// beats (city/beatGrid): live multi-camera coverage that cuts the way the music
// asks.
//
// Khari, 2026-10-28: *"Analyze the song and the genre. If the song requires a
// lot of movement, simulate multiple live cameras. Whip pan onto a closeup of
// the artists face.. change shots rapidly based on the drum pattern, etc.."*
//
// THE SWITCHER. Eight cameras, as a live show is covered: a jib at the back of
// the square, a steadicam sweeping the front, a long lens in the pit on each
// side, a handheld on each wing of the stage, one behind the performer looking
// out, one down in the crowd. A cut picks the next camera (never the same one
// twice; across to the other side more often than not) and, for the pit, how
// tight.
//
// THE PACE comes from each bar's intensity (how loud against the rest of the
// song, how hard the kick lands, how busy the drums are), scaled by the song's
// drive (how much movement it asks for at all — a ballad's loudest chorus still
// cuts slower than a club track's verse):
//     quiet      a shot every four bars, wide
//     building   two bars, one bar
//     driving    every two beats, every beat
//     peak       every beat, plus a cut on the off-beat snare
// Long shots cut on the bar, short ones on the beat, and every cut lands on the
// real kick or snare nearest it (beatGrid `hits`), so it hits with the drum.
//
// WHERE THE SONG LIFTS (the loudness jumps into a chorus or a drop) the first
// cut is a whip pan onto a close-up of their face, or a crash zoom from the jib,
// in turn. At the peak a few more cuts whip, always onto a face.
//
// WHAT THE SONG IS, not only how fast (Khari, 2026-10-29: *"The plaza show's
// scene view is treating slow RnB songs with fast paced cut scenes similar to
// the rock songs. We should analyze more than just the tempo. Consider genre
// and sentiment if possible."*). The set carries each song's FEEL (API
// CityStageService feelOf): a director's call, made once per song from its
// genre, mood and how its sections move — camera movement from languid to
// frenetic, and how much it holds the face — plus its sections' energy and
// where it is sung. The pace is mostly that call, a quarter the measured audio.
// A slow song holds two bars a shot at least, never whips or crashes, pushes in
// gently at its lifts and leans on the steadicam and the jib; close-ups follow
// the singing (as much as its face time asks) and the wides take the
// instrumental stretches. Without a feel (a song not yet judged) the audio
// alone sets the pace, as before.
//
// DETERMINISTIC: the same song, feel and seed give the same cuts, frame for frame.

const clamp = (x, a = 0, b = 1) => Math.min(b, Math.max(a, x));

function rng(seed) {
  let a = seed >>> 0;
  return () => {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// The cameras: the stage shot each one is (city/sceneShots planStageShot) and
// how often it is wanted when the music is [quiet, driving, at its peak].
export const CAMS = {
  jib:    { kind: 'jib', w: [3, 1.4, 0.7] },
  steadi: { kind: 'sweep', w: [2, 1.2, 0.5] },
  pitL:   { kind: 'pit', turn: -1, w: [2, 2.6, 3], face: true },
  pitR:   { kind: 'pit', turn: 1, w: [2, 2.6, 3], face: true },
  wingL:  { kind: 'wing', turn: -1, w: [0.8, 1.6, 2.2], face: true },
  wingR:  { kind: 'wing', turn: 1, w: [0.8, 1.6, 2.2], face: true },
  rear:   { kind: 'reverse', w: [1, 0.8, 0.8] },
  crowd:  { kind: 'crowd', w: [1, 1.4, 1.8] },
};
const OTHER_SIDE = { pitL: 'pitR', pitR: 'pitL', wingL: 'wingR', wingR: 'wingL' };
// How tight the pit's lens is, by band: [medium, close, tight].
const PIT_FRAMING = [[0.55, 0.45, 0], [0.3, 0.5, 0.2], [0.12, 0.5, 0.38]];
const MIN_SHOT = 0.45;    // s — no shot shorter (at 150 BPM a beat is 0.4 s)
// The director's call (feel.motion.energy) as a pace, and what a section's energy word is worth.
const MOTION_PACE = { languid: 0.35, smooth: 0.5, steady: 0.66, dynamic: 0.84, kinetic: 1.0, frenetic: 1.12 };
const SECTION_ENERGY = { low: 0.25, building: 0.55, peak: 0.9, declining: 0.4 };
const FACE_BOOST = { low: 1, medium: 1.35, high: 1.8 };
const SMOOTH_CAMS = new Set(['jib', 'steadi']);
const SNAP = 0.07;        // s — how far a cut moves to land on a real hit

/** The beats a shot lasts at this intensity. */
const beatsPerShot = (i) => (i < 0.25 ? 16 : i < 0.42 ? 8 : i < 0.58 ? 4 : i < 0.74 ? 2 : 1);
const bandOf = (i) => (i < 0.42 ? 0 : i < 0.68 ? 1 : 2);

/** The last index in a sorted list at or before `s`, or -1. */
export function lastAtOrBefore(list, s, key = (v) => v) {
  let lo = 0; let hi = list.length - 1; let ans = -1;
  while (lo <= hi) { const m = (lo + hi) >> 1; if (key(list[m]) <= s) { ans = m; lo = m + 1; } else hi = m - 1; }
  return ans;
}
function nearest(list, s, within) {
  const i = lastAtOrBefore(list, s);
  let best = null; let d = within;
  for (const j of [i, i + 1]) { const v = list[j]; if (v != null && Math.abs(v - s) <= d) { d = Math.abs(v - s); best = v; } }
  return best;
}
/** 1 on a hit, decaying (for the handheld's kick and the lens's snare punch). */
export function hitEnv(list, s, decay = 0.09) {
  const i = lastAtOrBefore(list || [], s);
  return i < 0 ? 0 : Math.exp(-(s - list[i]) / decay);
}

/** The pace a song is cut at, 0.35..1.1: the director's call when there is one, the audio's drive otherwise. */
export function paceOf(A, feel) {
  const audio = 0.4 + 0.7 * (A?.drive ?? 0.5);
  const call = MOTION_PACE[feel?.motion?.energy];
  return call != null ? 0.75 * call + 0.25 * audio : audio;
}

/**
 * @param A     beatGrid measureSong() result
 * @param seed  e.g. the track id (the same song cuts the same way every time)
 * @param feel  the song's feel from the set (API CityStageService feelOf), or null
 * @returns { cuts: [{ t, cam, kind, turn, framing, whip, snap, push, jolt, band }], intensity: [per bar], pace } | null
 *   t is seconds into the song; cuts are in order, the first at 0.
 */
export function planCuts(A, seed = 1, feel = null) {
  const bars = A?.bars;
  if (!bars?.length || !A.beat) return null;
  const R = rng(seed);
  const beat = A.beat;
  const kicks = A.hits?.kick || []; const snares = A.hits?.snare || [];
  const pace = paceOf(A, feel);
  // Where the song is sung (its lyric lines), else which sections have vocals.
  const sung = Array.isArray(feel?.sung) && feel.sung.length ? feel.sung : null;
  const sections = Array.isArray(feel?.sections) ? feel.sections : [];
  const sectionAt = (s) => sections.find((x) => s * 1000 >= x.s && s * 1000 < x.e) || null;
  const isSung = (s) => (sung ? sung.some(([a, b]) => s * 1000 >= a && s * 1000 < b) : (sectionAt(s)?.vocals ?? true));
  const faceBoost = FACE_BOOST[feel?.motion?.faceTime] ?? 1.2;
  const raw = bars.map((b) => {
    const audio = clamp(0.12 + 0.55 * b.e + 0.18 * b.punch + 0.15 * b.busy);
    const sec = SECTION_ENERGY[sectionAt(b.t + b.len / 2)?.energy];
    return sec != null ? 0.65 * audio + 0.35 * sec : audio;
  });
  const intensity = raw.map((v, k) => clamp((0.25 * (raw[k - 1] ?? v) + 0.5 * v + 0.25 * (raw[k + 1] ?? v)) * (0.4 + 0.75 * pace)));
  // The shortest shot the song allows, in beats: four bars for the slowest, two for a smooth one.
  const minBeats = pace < 0.45 ? 16 : pace < 0.6 ? 8 : pace < 0.78 ? 4 : pace < 0.95 ? 2 : 1;
  const whips = pace >= 0.75; const crashes = pace >= 0.7; const jolts = pace >= 0.7;
  // Where the song lifts (the loudness jumps), no closer than four bars apart:
  // a whip, a crash or a push is an event, and every small rise is not one.
  const lifts = [];
  for (let k = 0, last = -99; k < bars.length; k++) {
    const up = k >= 2 && bars[k].e - (bars[k - 1].e + bars[k - 2].e) / 2 >= 0.25 && k - last >= 4;
    lifts.push(up);
    if (up) last = k;
  }

  const cuts = [];
  let liftCount = 0;
  const choose = (band, prev, wantFace, t = 0) => {
    const opts = Object.entries(CAMS).filter(([id, c]) => id !== prev && (!wantFace || c.face));
    const singing = isSung(t);
    const w = opts.map(([id, c]) => c.w[band]
      * (OTHER_SIDE[prev] === id ? 1.4 : 1)
      // A slow song leans on the moving cameras that glide, not the handhelds.
      * (pace < 0.65 ? (SMOOTH_CAMS.has(id) ? 1.6 : (id.startsWith('wing') || id === 'crowd') ? 0.55 : 1) : 1)
      // Faces while they sing (as much as the song's face time asks); the room when they don't.
      * (c.face ? (singing ? faceBoost : 0.6) : (singing ? 1 : 1.4)));
    let r = R() * w.reduce((a, b) => a + b, 0);
    for (let i = 0; i < opts.length; i++) { r -= w[i]; if (r <= 0) return opts[i][0]; }
    return opts[opts.length - 1][0];
  };
  const framingFor = (band) => {
    const p = PIT_FRAMING[band]; const r = R();
    return r < p[0] ? 'medium' : r < p[0] + p[1] ? 'close' : 'tight';
  };
  const add = (t, cam, extra = {}) => {
    const prev = cuts[cuts.length - 1];
    if (prev && t - prev.t < MIN_SHOT) return null;
    const c = CAMS[cam];
    const band = extra.band ?? 0;
    const cut = { t: Math.round(t * 1000) / 1000, cam, kind: c.kind, turn: c.turn ?? (R() < 0.5 ? -1 : 1), framing: c.kind === 'pit' ? (extra.framing || framingFor(band)) : null, whip: !!extra.whip, snap: !!extra.snap, push: !!extra.push, jolt: jolts && band > 0, band };
    cuts.push(cut);
    return cut;
  };

  // The song opens on the jib, and holds it until the first bar has played.
  add(0, 'jib');
  let due = Math.max(bars[0].t + bars[0].len, 2 * beat * 4 * 0.5);

  for (let k = 0; k < bars.length; k++) {
    const b = bars[k];
    const I = intensity[k]; const band = bandOf(I);
    const per = Math.max(beatsPerShot(I), minBeats, Math.ceil(MIN_SHOT / beat));
    const step = b.len / 4;

    // A LIFT: cut on its downbeat — whip onto a face, or crash in from the jib;
    // in a slow song, a gentle push in on their face instead.
    if (lifts[k] && I >= 0.42) {
      const t = nearest(kicks, b.t, SNAP) ?? b.t;
      const prev = cuts[cuts.length - 1];
      const other = prev?.cam === 'pitL' ? 'pitR' : prev?.cam === 'pitR' ? 'pitL' : (R() < 0.5 ? 'pitL' : 'pitR');
      let made;
      if (!crashes) made = add(t, other, { band, push: true, framing: 'close' });
      else {
        const whip = whips && liftCount % 2 === 0;
        liftCount++;
        const cam = whip ? other : (prev?.cam === 'jib' ? 'pitL' : 'jib');
        made = add(t, cam, { band, whip, snap: !whip, framing: whip ? (band === 2 ? 'tight' : 'close') : null });
      }
      if (made) { due = t + per * beat; continue; }
    }

    // The bar's cut points: the kick on the beat at this pace — and at the peak,
    // the snare where it hits too, so the cuts trade kick, snare, kick, snare.
    const peak = band === 2 && I >= 0.84 && pace >= 0.95;
    const cadence = peak ? Math.max(2, per) : per;
    const cand = [];
    for (let q = 0; q < 4; q++) {
      if (cadence >= 4 && q !== 0) continue;            // long shots cut on the bar
      if (cadence === 2 && q % 2) continue;             // two-beat shots on 1 and 3
      cand.push({ grid: b.t + q * step, on: 'kick' });
    }
    if (peak && b.snare) for (const st of [4, 12, 14, 6, 10, 2]) if ((b.snare >> st) & 1) cand.push({ grid: b.t + (st * b.len) / 16, on: 'snare' });
    cand.sort((p, q) => p.grid - q.grid);
    for (const c of cand) {
      if (c.on === 'kick' && c.grid < due - 0.5 * beat) continue;
      const t = nearest(c.on === 'kick' ? kicks : snares, c.grid, SNAP) ?? c.grid;
      const prev = cuts[cuts.length - 1];
      if (prev && t <= prev.t) continue;
      // At the peak, some cuts whip, and a whip always lands on a face.
      // (Rare on purpose: one in every dozen or so peak cuts. A whip is an event.)
      const whip = whips && band === 2 && !prev?.whip && R() < 0.08;
      const cam = choose(band, prev?.cam, whip, t);
      if (add(t, cam, { band, whip }) && c.on === 'kick') due = t + cadence * beat;
    }
  }
  return { cuts, intensity, pace };
}
